import {useMemo, useState} from "react"
import {Alert, Badge, Button as AntButton, Drawer, Empty, Skeleton} from "antd"
import {ArrowLeftOutlined, ReloadOutlined} from "@ant-design/icons"
import {useTranslation} from "react-i18next"
import {Button} from "../../components/ui/button"
import {ENABLE_NOTIFICATIONS} from "../../utils/featureFlags"
import {getAdminIntlLocale, getAdminLanguage, isAdminLanguage} from "../../utils/language"
import {
    AdminNotification,
    CONTENTFUL_NOTIFICATIONS_CONFIG,
    getNotificationText
} from "./contentfulNotifications"
import {useAdminNotifications} from "./useAdminNotifications"

interface AdminNotificationsCenterProps {
    accountId: number | null
    icon: React.ReactNode
}

const formatPublishedAt = (value: string, language: "ru" | "uz") => {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ""
    return new Intl.DateTimeFormat(getAdminIntlLocale(language), {
        dateStyle: "medium",
        timeStyle: "short"
    }).format(date)
}

interface ConfiguredNotificationsCenterProps extends Omit<AdminNotificationsCenterProps, "accountId"> {
    accountId: number
}

const ConfiguredNotificationsCenter = ({accountId, icon}: ConfiguredNotificationsCenterProps) => {
    const {t, i18n} = useTranslation()
    const language = isAdminLanguage(i18n.resolvedLanguage) ? i18n.resolvedLanguage : getAdminLanguage()
    const [open, setOpen] = useState(false)
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const {
        notifications,
        readIds,
        unreadCount,
        isLoading,
        isRefreshing,
        error,
        refresh,
        refreshIfStale,
        markAsRead,
        markAllAsRead
    } = useAdminNotifications({accountId, config: CONTENTFUL_NOTIFICATIONS_CONFIG!})
    const selectedNotification = useMemo(
        () => notifications.find((notification) => notification.id === selectedId) || null,
        [notifications, selectedId]
    )

    const openNotifications = () => {
        setOpen(true)
        void refreshIfStale()
    }

    const openDetails = (notification: AdminNotification) => {
        markAsRead(notification.id)
        setSelectedId(notification.id)
    }

    const closeDrawer = () => {
        setOpen(false)
        setSelectedId(null)
    }

    return (
        <>
            <Badge
                className="admin-notifications-badge"
                count={unreadCount}
                overflowCount={99}
                size="small"
                offset={[-2, 3]}
            >
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="app-header-icon-button"
                    aria-label={t("common.notifications")}
                    onClick={openNotifications}
                >
                    {icon}
                </Button>
            </Badge>

            <Drawer
                rootClassName="admin-notifications-drawer"
                title={t("notificationsCenter.title")}
                width="min(480px, 100vw)"
                open={open}
                onClose={closeDrawer}
                extra={!selectedNotification && unreadCount > 0 ? (
                    <AntButton type="text" size="small" onClick={markAllAsRead}>
                        {t("notificationsCenter.markAllRead")}
                    </AntButton>
                ) : null}
            >
                {selectedNotification ? (
                    <article className="admin-notification-detail">
                        <AntButton
                            type="text"
                            className="admin-notification-detail__back"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => setSelectedId(null)}
                        >
                            {t("notificationsCenter.backToList")}
                        </AntButton>
                        {selectedNotification.imageUrl ? (
                            <img
                                className="admin-notification-detail__image"
                                src={selectedNotification.imageUrl}
                                alt=""
                            />
                        ) : null}
                        <div className="admin-notification-detail__date">
                            {formatPublishedAt(selectedNotification.publishedAt, language)}
                        </div>
                        <h2>{getNotificationText(selectedNotification.title, language)}</h2>
                        <div className="admin-notification-detail__text">
                            {getNotificationText(selectedNotification.details, language)
                                || getNotificationText(selectedNotification.summary, language)}
                        </div>
                    </article>
                ) : (
                    <div className="admin-notifications-list" aria-live="polite">
                        {error ? (
                            <Alert
                                className="admin-notifications-list__alert"
                                type="error"
                                showIcon
                                message={t("notificationsCenter.loadError")}
                                action={(
                                    <AntButton size="small" icon={<ReloadOutlined />} loading={isLoading || isRefreshing} onClick={() => void refresh()}>
                                        {t("notificationsCenter.retry")}
                                    </AntButton>
                                )}
                            />
                        ) : null}

                        {isLoading && notifications.length === 0 ? Array.from({length: 4}, (_, index) => (
                            <div className="admin-notification-skeleton" key={index}>
                                <Skeleton active title={{width: "55%"}} paragraph={{rows: 2}} />
                            </div>
                        )) : null}

                        {!isLoading && notifications.length === 0 && !error ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("notificationsCenter.empty")} />
                        ) : null}

                        {notifications.map((notification) => {
                            const isRead = readIds.has(notification.id)
                            const title = getNotificationText(notification.title, language)
                            const summary = getNotificationText(notification.summary, language)
                            return (
                                <button
                                    type="button"
                                    className={`admin-notification-row${notification.imageUrl ? " admin-notification-row--with-image" : ""}${isRead ? " admin-notification-row--read" : " admin-notification-row--unread"}`}
                                    key={notification.id}
                                    onClick={() => openDetails(notification)}
                                >
                                    <span className="admin-notification-row__content">
                                        <span className="admin-notification-row__meta">
                                            <span className="admin-notification-row__date">
                                                {formatPublishedAt(notification.publishedAt, language)}
                                            </span>
                                            {!isRead ? (
                                                <span className="admin-notification-row__new">
                                                    <span className="admin-notification-row__dot" />
                                                    {t("notificationsCenter.new")}
                                                </span>
                                            ) : null}
                                        </span>
                                        <span className="admin-notification-row__title">{title}</span>
                                        {summary ? <span className="admin-notification-row__summary">{summary}</span> : null}
                                    </span>
                                    {notification.imageUrl ? (
                                        <img className="admin-notification-row__image" src={notification.imageUrl} alt="" loading="lazy" />
                                    ) : null}
                                </button>
                            )
                        })}
                    </div>
                )}
            </Drawer>
        </>
    )
}

const AdminNotificationsCenter = ({accountId, icon}: AdminNotificationsCenterProps) => {
    if (!ENABLE_NOTIFICATIONS || !CONTENTFUL_NOTIFICATIONS_CONFIG || accountId === null) return null
    return <ConfiguredNotificationsCenter accountId={accountId} icon={icon} />
}

export default AdminNotificationsCenter
